import React, { useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/css";
import Button from "../../components/general/Button";
import CustomScrollbars from "../../components/structure/CustomScrollbars";
import ScheduledAnalyticsEmailContainer from "../../hooks/ScheduledAnalyticsEmailContainer";
import TextField from "../../components/forms/TextField";
import StyledDropdown from "../../scenes/applicationSettings/components/customAssistantConfigurations/StyledDropdown";
import { getApplicationsForOrg } from "../../api";
import MetricsToExcludeModal from "./MetricsToExcludeModal";
import {
    color_shades_darkest,
    color_shades_light,
    color_defaults_white,
    cool_grey,
} from "../../constants/colors";
import { style_border_default } from "../../constants/stylesValues";
import ApplicationModel from "../../models/applications/api/ApplicationModel";
import { FoodOrderingFeatureFlagId, HealthcareSchedulingFeatureFlagId } from "../../constants/featureFlagIds";
import ApplicationContainer from "../../state/containers/ApplicationContainer";
import SettingsCard from "../structure/SettingsCard";

const trashIcon = require("../../../shared/content/images/trash.svg");

interface Props {
    organizationId: string;
    orgName: string;
    appContainer: ApplicationContainer;
    isSparkView?: boolean;
}

/** A thin editable view model for a row in the table */
interface EditableEmailRow {
    tempId?: number;
    id?: string; // undefined means it's a new row (to create)
    organizationId: string;
    applicationId: string | null;
    email: string;
    dayToReviewFrom?: string;
    dateOfNextEmail?: string; // ISO (yyyy-mm-dd) for <input type="date">
    daysTillNextEmail?: number;
    metricsToExclude?: string[];
    _isDeleted?: boolean; // local marker for deletion prior to save
    _isNew?: boolean; // local marker for creation
    _dirty?: boolean; // local marker for an edited row
}

const ScheduledEmailForm: React.FC<Props> = ({ organizationId, orgName, appContainer, isSparkView }) => {
    const scheduled = ScheduledAnalyticsEmailContainer.useContainer();
    const [applications, setApplications] = useState<ApplicationModel[]>([]);
    const [metricsToExcludeRow, setMetricsToExcludeRow] = useState<EditableEmailRow>(null);
    const [featureFlag, setFeatureFlag] = useState(null);
    // Local editing state
    const [rows, setRows] = useState<EditableEmailRow[]>([]);
    const [filterEmail, setFilterEmail] = useState<string>("__all__");
    const [nextDatesUpdated, setNextDatesUpdated] = useState<Set<number>>(new Set());

    const DAY_TO_REVIEW_FROM_OPTIONS = [
        { label: "Last Email", value: "LastEmail" },
        { label: "First of the month", value: "FirstOfMonth" },
        { label: "First of the year", value: "FirstOfYear" },
    ];

    // Load org rows on mount if not already present
    useEffect(() => {
        if (!organizationId) return;
        scheduled.loadForOrganization(organizationId);
    }, [organizationId]);

    useEffect(() => {
        //Load all the apps for the org
        (async () => {
            const result = await getApplicationsForOrg(organizationId);
            const appsToKeep = result.data.filter(
                (e) => !e.name.includes("Shared Topics") && !e.name.includes("Manage Menu")
            );
            if (result.resultType === "Ok" && result.data) setApplications(appsToKeep);
        })();
    }, [organizationId]);

    useEffect(() => {
        (async () => {
            const result = await appContainer.getApplicationsForOrg(organizationId);
            if (result.resultType !== "Ok") {
                setFeatureFlag(FoodOrderingFeatureFlagId);
                return;
            }
            const featureFlags = result.data.map((app) => app.featureFlags).flat();
            if (featureFlags.filter((ff) => ff.id === FoodOrderingFeatureFlagId)?.length > 0)
                setFeatureFlag(FoodOrderingFeatureFlagId);
            else if (featureFlags.filter((ff) => ff.id === HealthcareSchedulingFeatureFlagId)?.length > 0)
                setFeatureFlag(HealthcareSchedulingFeatureFlagId);
            else setFeatureFlag(FoodOrderingFeatureFlagId);
        })();
    }, [organizationId]);

    const toISODate = (d: Date) => {
        const y = d.getUTCFullYear();
        const m = String(d.getUTCMonth() + 1).padStart(2, "0");
        const day = String(d.getUTCDate()).padStart(2, "0");
        const out = `${y}-${m}-${day}`;
        return out;
    };

    const addDaysUTC = (d: Date, days: number) => {
        // normalize to UTC midnight to avoid TZ drift
        const base = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
        base.setUTCDate(base.getUTCDate() + days);
        return base;
    };

    const initialRows = useMemo(() => {
        const rows = scheduled
            .getForOrganization(organizationId, "created", "desc")
            .map((e, index) => {
                const hasStart = e?.emailStartDate != null;
                const startDate = hasStart ? new Date(e.emailStartDate) : null; // handles Date or ISO string
                const days = Number((e as any).daysTillNextEmail ?? 7);
                let nextDate: Date | null = null;

                if (startDate) {
                    if (e.numberEmailsSent > 0) {
                        const lastSent = addDaysUTC(startDate, days * e.numberEmailsSent);
                        nextDate = addDaysUTC(lastSent, days);
                    } else {
                        // first send is at the configured start date
                        nextDate = startDate;
                    }
                }

                return {
                    tempId: index,
                    id: e.id,
                    organizationId: e.organizationId,
                    applicationId: e.applicationId ?? null,
                    email: (e as any).email ?? "",
                    dateOfNextEmail: nextDate ? toISODate(nextDate) : undefined, // "YYYY-MM-DD"
                    dayToReviewFrom: e.dayToReviewFrom ?? null,
                    daysTillNextEmail: days,
                    metricsToExclude: (e as any).metricsToExclude ?? [],
                } as EditableEmailRow;
            });
        return rows;
    }, [organizationId, scheduled.emails]);

    useEffect(() => {
        setRows(initialRows);
    }, [initialRows.length]); // eslint-disable-line react-hooks/exhaustive-deps

    // Build dropdown options for App scope, first option is "Full Organization"
    const appScopeOptions = useMemo(
        () => [
            { label: "Full Organization", value: "__org__" },
            ...applications
                .map((a) => ({ label: a.name, value: a.id }))
                .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" })),
        ],
        [applications]
    );

    const appNameById = useMemo(() => {
        const map: Record<string, string> = {};
        for (const a of applications) map[a.id] = a.name;
        return map;
    }, [applications]);

    // Filter by recipient selector (unique recipients)
    const uniqueRecipients = useMemo(() => {
        const set = new Set(rows.map((r) => r.email).filter(Boolean));
        return ["__all__", ...Array.from(set)];
    }, [rows]);

    const filteredRows = useMemo(() => {
        if (filterEmail === "__all__") return rows.filter((r) => !r._isDeleted);
        return rows.filter((r) => !r._isDeleted && r.email === filterEmail);
    }, [rows, filterEmail]);

    const onChangeRow = (index: number, patch: Partial<EditableEmailRow>) => {
        setRows((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], ...patch, _dirty: true };
            return next;
        });
    };

    const onAddRow = () => {
        const prevId = rows?.length > 0 ? rows[rows.length - 1].tempId : -1;
        setRows((prev) => [
            {
                tempId: prevId + 1,
                _isNew: true,
                organizationId: organizationId,
                id: null,
                applicationId: null,
                email: "placeholder@email.com",
                dayToReviewFrom: null,
                dateOfNextEmail: new Date().toISOString(),
                daysTillNextEmail: 7,
                metricsToExclude: [],
            },
            ...prev,
        ]);
    };

    const onRemoveRow = (index: number) => {
        setRows((prev) => {
            const row = prev[index];
            // New rows can be dropped immediately; existing rows are marked for delete
            if (row._isNew) {
                return prev.filter((_, i) => i !== index);
            }
            const next = [...prev];
            next[index] = { ...row, _isDeleted: true, _dirty: true };
            return next;
        });
    };

    const emailValidator = (value: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(value) ? null : "Please enter a valid email address";
    };

    const saveAll = async () => {
        // Save in three phases: deletes, updates, creates
        // (So that new rows don't conflict and deletes happen first)
        const toDelete = rows.filter((r) => r.id && r._isDeleted);
        const toUpdate = rows.filter((r) => r.id && !r._isDeleted && r._dirty);
        const toCreate = rows.filter((r) => r._isNew && !r._isDeleted);

        // deletes
        for (const r of toDelete) {
            await scheduled.remove(r.id!);
        }

        // updates
        for (const r of toUpdate) {
            const dateOfNext = r.dateOfNextEmail ? new Date(r.dateOfNextEmail) : null;

            //If the next date to send or frequency is changed, reset the start date and number of emails sent
            if (nextDatesUpdated.has(rows.indexOf(r))) {
                await scheduled.update({
                    id: r.id!,
                    organizationId: r.organizationId,
                    applicationId: r.applicationId,
                    email: r.email,
                    emailStartDate: dateOfNext,
                    numberEmailsSent: 0,
                    dayToReviewFrom: r.dayToReviewFrom,
                    daysTillNextEmail: r.daysTillNextEmail,
                    metricsToExclude: r.metricsToExclude,
                } as any);
            } else {
                await scheduled.update({
                    id: r.id!,
                    organizationId: r.organizationId,
                    applicationId: r.applicationId,
                    email: r.email,
                    dayToReviewFrom: r.dayToReviewFrom,
                    daysTillNextEmail: r.daysTillNextEmail,
                    metricsToExclude: r.metricsToExclude,
                } as any);
            }
        }

        // creates
        for (const r of toCreate) {
            const dateOfNext = r.dateOfNextEmail ? new Date(r.dateOfNextEmail) : new Date();

            await scheduled.create({
                organizationId: r.organizationId,
                applicationId: r.applicationId,
                email: r.email,
                emailStartDate: dateOfNext,
                dayToReviewFrom: r.dayToReviewFrom,
                daysTillNextEmail: r.daysTillNextEmail,
                metricsToExclude: r.metricsToExclude,
            } as any);
        }
    };

    const isSaveDisabled = useMemo(() => {
        // basic validation: all non-deleted rows must have recipient email
        for (const r of rows) {
            if (r._isDeleted) continue;
            if (!r.email?.trim()) return true;
        }
        return false;
    }, [rows]);

    return (
        <SettingsCard
            title="Email Configuration"
            subtitle="Configure scheduled email updates about assistants and organization-wide preformance"
        >
            <EmailConfigurationWrapper>
                <ControlsRow>
                    <LeftControls>
                        <FilterLabel>Filter by Recipient</FilterLabel>
                        <StyledFilterDropdown
                            options={uniqueRecipients.map((e) => ({
                                label: e === "__all__" ? "All Recipients" : e,
                                value: e,
                            }))}
                            value={filterEmail}
                            handleOnChange={(_, v) => setFilterEmail(String(v))}
                            size="small"
                            openMenuAbove={false}
                        />
                    </LeftControls>
                </ControlsRow>

                <TableShell>
                    <HeaderRow>
                        <TableLabelWrapper>Recipient</TableLabelWrapper>
                        <TableLabelWrapper>Scope</TableLabelWrapper>
                        <TableLabelWrapper>Begin Review</TableLabelWrapper>
                        <TableLabelWrapper>Frequency (Days)</TableLabelWrapper>
                        <TableLabelWrapper>Metrics</TableLabelWrapper>
                        <TableLabelWrapper />
                    </HeaderRow>

                    <Scrollable>
                        <CustomScrollbars autoHide autoHeight autoHeightMax={420}>
                            {filteredRows.length === 0 || filteredRows === null ? (
                                <EmptyState>No rows to display.</EmptyState>
                            ) : (
                                filteredRows.map((r, visibleIndex) => {
                                    // map back to its true index in rows
                                    const rowIndex = rows.findIndex(
                                        (x) => x === filteredRows[visibleIndex]
                                    );
                                    return (
                                        <BodyRow
                                            key={rowKey(r, rowIndex)}
                                            isTop={rowIndex === 0}
                                            isBottom={rowIndex === filteredRows?.length - 1}
                                        >
                                            <RowComponent>
                                                <StyledTextField
                                                    height="33"
                                                    id={`${rowIndex}`}
                                                    name={`recipient-${rowIndex}`}
                                                    value={r.email}
                                                    type="email"
                                                    placeholder="email@company.com"
                                                    onChange={(e: any) =>
                                                        onChangeRow(rowIndex, { email: e.target.value })
                                                    }
                                                    validate={emailValidator}
                                                    useForm={false}
                                                    fontSize={13}
                                                />
                                            </RowComponent>

                                            <RowComponent>
                                                <StyledTableDropdown
                                                    fieldName={`app-${rowIndex}`}
                                                    value={r.applicationId ?? "__org__"}
                                                    options={appScopeOptions}
                                                    searchable={true}
                                                    size="small"
                                                    handleOnChange={(_, v) =>
                                                        onChangeRow(rowIndex, {
                                                            applicationId:
                                                                v === "__org__" ? null : String(v),
                                                        })
                                                    }
                                                    openMenuAbove={false}
                                                />
                                            </RowComponent>

                                            <RowComponent>
                                                <StyledTableDropdown
                                                    fieldName={`review-${rowIndex}`}
                                                    value={r.dayToReviewFrom}
                                                    options={DAY_TO_REVIEW_FROM_OPTIONS}
                                                    size="small"
                                                    handleOnChange={(_, v) =>
                                                        onChangeRow(rowIndex, {
                                                            dayToReviewFrom: String(v),
                                                        })
                                                    }
                                                    openMenuAbove={false}
                                                />
                                            </RowComponent>

                                            <RowComponent>
                                                <Incrementer>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const updatedSet = new Set(nextDatesUpdated);
                                                            updatedSet.add(rowIndex);
                                                            setNextDatesUpdated(updatedSet);
                                                            onChangeRow(rowIndex, {
                                                                daysTillNextEmail: Math.max(
                                                                    1,
                                                                    (r.daysTillNextEmail ?? 1) - 1
                                                                ),
                                                            });
                                                        }}
                                                    >
                                                        −
                                                    </button>
                                                    <input
                                                        type="number"
                                                        min={1}
                                                        value={r.daysTillNextEmail ?? 1}
                                                        onChange={(e) => {
                                                            const updatedSet = new Set(nextDatesUpdated);
                                                            updatedSet.add(rowIndex);
                                                            setNextDatesUpdated(updatedSet);
                                                            onChangeRow(rowIndex, {
                                                                daysTillNextEmail: Math.max(
                                                                    1,
                                                                    Number(e.target.value) || 1
                                                                ),
                                                            });
                                                        }}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const updatedSet = new Set(nextDatesUpdated);
                                                            updatedSet.add(rowIndex);
                                                            setNextDatesUpdated(updatedSet);
                                                            onChangeRow(rowIndex, {
                                                                daysTillNextEmail:
                                                                    (r.daysTillNextEmail ?? 1) + 1,
                                                            });
                                                        }}
                                                    >
                                                        +
                                                    </button>
                                                </Incrementer>
                                            </RowComponent>

                                            <RowComponent>
                                                <PreviewButton
                                                    themes={["secondary-small", "fill"]}
                                                    onClick={() => setMetricsToExcludeRow(r)}
                                                    text={"Preview"}
                                                />
                                            </RowComponent>

                                            <RowComponent>
                                                <RemoveButton
                                                    type="button"
                                                    onClick={() => onRemoveRow(rowIndex)}
                                                    title="Remove row"
                                                >
                                                    <img src={trashIcon} alt="Remove" />
                                                </RemoveButton>
                                            </RowComponent>

                                            {metricsToExcludeRow === r && (
                                                <MetricsToExcludeModal
                                                    isVisible={metricsToExcludeRow === r}
                                                    orgId={organizationId}
                                                    orgName={orgName}
                                                    appContainer={appContainer}
                                                    initialSelected={r.metricsToExclude}
                                                    onSave={(selected) => {
                                                        setMetricsToExcludeRow(null);
                                                        onChangeRow(rowIndex, {
                                                            metricsToExclude: selected,
                                                        });
                                                    }}
                                                    onClose={() => setMetricsToExcludeRow(null)}
                                                    featureFlag={featureFlag}
                                                    isSparkView={isSparkView}
                                                />
                                            )}
                                        </BodyRow>
                                    );
                                })
                            )}
                        </CustomScrollbars>
                    </Scrollable>
                </TableShell>

                <ControlsRow>
                    <AddRowWrapper>
                        <AddRowButton
                            themes={["secondary"]}
                            type="button"
                            text="+ Add Row"
                            onClick={onAddRow}
                            className={addRowClass}
                        />
                    </AddRowWrapper>
                    <RightControls>
                        <SaveChangesButton
                            themes={["primary", "start"]}
                            text="Save Changes"
                            onClick={saveAll}
                            disabled={scheduled.isLoading || isSaveDisabled}
                            loading={scheduled.isLoading}
                        />
                    </RightControls>
                </ControlsRow>
            </EmailConfigurationWrapper>
        </SettingsCard>
    );
};

function rowKey(r: EditableEmailRow, idx: number) {
    return r.id ?? `new-${idx}`;
}

/* ====== STYLES (RESPONSIVE) ====== */

const SaveChangesButton = styled(Button)`
    margin-right: 0;

    @media (max-width: 768px) {
        width: auto;
        min-width: 140px;
    }

    @media (max-width: 480px) {
        width: 100%;
        max-width: 200px;
    }
`;

const AddRowWrapper = styled.div`
    margin-left: 0;

    @media (max-width: 768px) {
        width: 100%;
        display: flex;
        justify-content: center;
    }
`;

const AddRowButton = styled(Button)`
    margin-left: 0;

    @media (max-width: 768px) {
        width: auto;
        min-width: 140px;
    }

    @media (max-width: 480px) {
        width: 100%;
        max-width: 200px;
    }
`;

const EmailConfigurationWrapper = styled.div`
    margin: 0;
    padding: 0;
    width: 100%;

    @media (max-width: 768px) {
        padding: 0 8px;
    }

    @media (max-width: 480px) {
        padding: 0 4px;
    }
`;

const ControlsRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 6px 0 12px 0;
    gap: 12px;
    flex-wrap: wrap;

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: stretch;
        gap: 16px;
        margin: 12px 0 16px 0;
    }

    @media (max-width: 480px) {
        margin: 8px 0 12px 0;
        gap: 12px;
    }
`;

const LeftControls = styled.div`
    display: flex;
    align-items: center;
    margin-left: 12px;
    gap: 12px;

    @media (max-width: 768px) {
        margin-left: 0;
        justify-content: center;
        flex-wrap: wrap;
        width: 100%;
    }

    @media (max-width: 480px) {
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
    }
`;

const RightControls = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;

    @media (max-width: 768px) {
        justify-content: center;
        width: 100%;
        flex-wrap: wrap;
    }

    @media (max-width: 480px) {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
    }
`;

const FilterLabel = styled.div`
    font-size: 13px;
    color: ${color_shades_darkest};
    position: relative;
    font-weight: 600;
    margin-right: 12px;
    white-space: nowrap;

    @media (max-width: 768px) {
        margin-right: 0;
        text-align: center;
        margin-bottom: 8px;
    }

    @media (max-width: 480px) {
        font-size: 14px;
        margin-bottom: 4px;
    }
`;

const TableShell = styled.div`
    position: relative;
    overflow-x: auto;
    width: 100%;
    border-radius: 8px;

    @media (max-width: 768px) {
        margin: 0 -8px;
        border-radius: 0;
    }

    @media (max-width: 480px) {
        margin: 0 -4px;
    }
`;

/**
 * Shared grid template for header + rows:
 *  1) Recipient
 *  2) Scope
 *  3) Begin Review
 *  4) Next Email Date
 *  5) Frequency
 *  6) Metrics
 *  7) Trash
 */
const tableGridTemplate = `
    minmax(180px, 1.8fr)
    minmax(180px, 1.4fr)
    minmax(180px, 1.4fr)
    minmax(120px, 1.1fr)
    minmax(120px, 1.1fr)
    auto
`;

const HeaderRow = styled.div`
    display: grid;
    grid-template-columns: ${tableGridTemplate};
    column-gap: 8px;
    padding: 8px 12px;
    border-bottom: ${style_border_default};
    background: ${color_shades_light};
    position: sticky;
    top: 0;
    z-index: 2;
    min-width: 900px;
`;

const TableLabelWrapper = styled.div`
    font-weight: 600;
    font-size: 13px;
    color: ${color_shades_darkest};
    padding: 4px 0;
    white-space: nowrap;
`;

const Scrollable = styled.div`
    max-height: 420px;
`;

const BodyRow = styled.div<{ isTop: boolean; isBottom: boolean }>`
    display: grid;
    grid-template-columns: ${tableGridTemplate};
    column-gap: 8px;
    padding: 8px 12px;
    background: ${color_defaults_white};
    border-left: ${style_border_default};
    border-right: ${style_border_default};
    border-bottom: ${style_border_default};
    border-top: ${(p) => (p.isTop ? style_border_default : "none")};
    min-width: 900px;

    border-radius: ${(p) =>
        p.isTop && p.isBottom
            ? "8px"
            : p.isBottom
                ? "0 0 8px 8px"
                : "0"};

    &[data-deleted="1"] {
        opacity: 0.55;
    }
`;

const addRowClass = css``;

const RowComponent = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
`;

const Incrementer = styled.div`
    display: inline-flex;
    align-items: center;
    border: ${style_border_default};
    border-radius: 6px;
    overflow: hidden;
    height: 36px;
    width: 100%;
    max-width: 160px;

    button {
        width: 36px;
        height: 36px;
        border: none;
        background: #f3f3f3;
        font-size: 18px;
        cursor: pointer;
        flex-shrink: 0;
    }

    input {
        flex: 1;
        min-width: 0;
        text-align: center;
        border: none;
        outline: none;
        height: 100%;
        font-size: 13px;
    }
`;

const RemoveButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;

    img {
        width: 16px;
        height: 16px;
    }
`;

const EmptyState = styled.div`
    padding: 16px;
    font-size: 13px;
    color: #666;
    text-align: center;

    @media (max-width: 768px) {
        padding: 24px 16px;
        font-size: 14px;
    }

    @media (max-width: 480px) {
        padding: 20px 12px;
    }
`;

const StyledTextField = styled(TextField) <{ height?: string }>`
    max-width: 75%;

    height: ${(props) => props.height || "auto"};
    .field-container {
        height: ${(props) => props.height || "auto"};
        width: 100%;
    }
    .field-container input {
        height: ${(props) => props.height || "auto"};
        width: 100%;
    }
`;

const StyledFilterDropdown = styled(StyledDropdown)`
    min-width: 200px;
    width: 100%;

    @media (max-width: 768px) {
        width: 100%;
        max-width: 300px;
    }

    @media (max-width: 480px) {
        min-width: auto;
        width: 100%;
    }
`;

const StyledTableDropdown = styled(StyledDropdown)`
    width: 100%;
    min-width: 180px;
`;

const PreviewButton = styled(Button)`
    width: 100%;
    max-width: 140px;
`;
export default ScheduledEmailForm;